import ReconnectingWebSocket, { ListenersMap } from "reconnecting-websocket";

import { BaseLogger } from "base_logger";
import { isTestEnv } from "config";
import { FlightAPI } from "contexts/flight_api_context";
import { Settings } from "settings";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";

type PubsubWebSocketOptions = {
  channel: string;
  flightAuthToken: string;
  userId: string;
  origin: "classroom" | "monitoring";
  serverUrl: string;
  monitoringServerUrl: string;
  path?: string;
};

export class PubsubWebSocket extends BaseLogger {
  public channel: string;

  public classId: string;

  public socketType: "classroom" | "monitoring";

  public flightAuthToken: string;

  public userId: string;

  public origin: "classroom" | "monitoring";

  public path?: string;

  public serverUrl: string;

  public monitoringServerUrl: string;

  public socket: ReconnectingWebSocket;

  public pingIntervalId?: number;

  constructor(options: PubsubWebSocketOptions) {
    super();
    this.channel = options.channel;
    PubsubWebSocket.log("Initializing PubsubWebSocket for channel:", this.channel);
    this.classId = this.channel.split("/")[1];
    this.flightAuthToken = options.flightAuthToken;
    this.userId = options.userId;
    this.origin = options.origin;
    this.serverUrl = options.serverUrl;
    this.monitoringServerUrl = options.monitoringServerUrl;
    this.path = options.path;
    this.socket = this.initializeWebSocket();
    this.socketType = this.channel.split("/")[0] as "classroom" | "monitoring";
  }

  addEventListener(event: keyof ListenersMap, callback: (event: MessageEvent) => void) {
    this.socket.addEventListener(event, callback);
  }

  initializeWebSocket() {
    if (!this.socket && !this.channel) {
      throw new Error("Socket being initialized without a channel.");
    }
    // Set up websocket
    const serverUrl = new URL(this.serverUrl);
    const isPresentationView =
      this.origin === "classroom" &&
      new URLSearchParams(window.location.search).has("presentation");
    const wsPath =
      this.path === undefined
        ? `/ws/channel/${this.channel}?userId=${this.userId}&origin=${this.origin}&isPresentationView=${isPresentationView}`
        : this.path;
    const socketUrl = `wss://${serverUrl.host}${wsPath}`;
    const socket = new ReconnectingWebSocket(socketUrl, [`authorization.${this.flightAuthToken}`], {
      // Reconnect to restarting dev server faster
      minReconnectionDelay: 1000,
    });

    socket.addEventListener("open", () => {
      if (window.location.pathname.startsWith("/classes/")) {
        const flightAPI = new FlightAPI(this.monitoringServerUrl, this.userId);
        flightAPI.postMonitoringEvent(this.classId, {
          type: MonitoringEventTypeType.PUBSUB_CONNECTED,
          userId: this.userId,
          contextData: {
            channel: this.channel,
          },
        });
      }

      // Send ping messages to the server to keep the connection alive
      // Note that we do it for all websocket connections, not just classroom but also monitoring
      this.pingIntervalId = window.setInterval(() => {
        socket.send(JSON.stringify({ type: "ping", startTimestamp: Date.now() }));
      }, Settings.PUBSUB_PING_INTERVAL);

      if (!isTestEnv) {
        PubsubWebSocket.log("Socket opened:", socket.url);
      }
    });

    socket.addEventListener("close", (event) => {
      if (window.location.pathname.startsWith("/classes/")) {
        const flightAPI = new FlightAPI(this.monitoringServerUrl, this.userId);
        flightAPI
          .postMonitoringEvent(this.classId, {
            type: MonitoringEventTypeType.PUBSUB_DISCONNECTED,
            userId: this.userId,
            contextData: {
              channel: this.channel,
              code: event.code,
              reason: event.reason,
            },
          })
          .catch((error) => {
            PubsubWebSocket.error("Error posting monitoring event:", error);
          });
      }
      if (!isTestEnv) {
        PubsubWebSocket.log("Socket closed:", socket.url);
      }
      if (this.pingIntervalId) {
        window.clearInterval(this.pingIntervalId);
      }
    });

    PubsubWebSocket.log("PubsubWebSocket initialized:", socket.url);

    return socket;
  }

  isOpen() {
    return this.socket.readyState === WebSocket.OPEN;
  }

  send(msg: string) {
    this.socket.send(msg);
  }
}
